import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { api, messageErreur } from "@/lib/api";
import { Champ, EchelleTemperature } from "@/components/commun";
import { Logo } from "@/components/shell/Logo";

const GoogleIcone = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true"><path fill="currentColor" d="M44.5 20H24v8.5h11.8C34.7 33.9 30.1 37 24 37c-7.2 0-13-5.8-13-13s5.8-13 13-13c3.1 0 5.9 1.1 8.1 2.9l6.4-6.4C34.6 4.1 29.6 2 24 2 11.8 2 2 11.8 2 24s9.8 22 22 22c11 0 21-8 21-22 0-1.3-.2-2.7-.5-4z" /></svg>
);

const MotDePasseOublie = ({ ouvert, setOuvert }) => {
  const [email, setEmail] = useState("");
  const envoyer = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/auth/mot-de-passe-oublie", { email });
      toast.success(data.message);
      setOuvert(false);
    } catch (err) { toast.error(messageErreur(err)); }
  };
  return (
    <Dialog open={ouvert} onOpenChange={setOuvert}>
      <DialogContent className="at-popover max-w-[calc(100vw-24px)] sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Mot de passe oublié</DialogTitle>
          <DialogDescription className="at-muted">Recevez un lien pour choisir un nouveau mot de passe.</DialogDescription>
        </DialogHeader>
        <form onSubmit={envoyer} className="space-y-4">
          <Champ label="E-mail" id="oubli-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email-input" />
          <button type="submit" className="at-btn at-btn-primary w-full" data-testid="forgot-submit-button">Envoyer le lien</button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default function Connexion() {
  const { connexion } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [mdp, setMdp] = useState("");
  const [erreur, setErreur] = useState("");
  const [envoi, setEnvoi] = useState(false);
  const [oubli, setOubli] = useState(false);
  const [google, setGoogle] = useState({ disponible: false, message: "" });

  useEffect(() => { api.get("/auth/google/statut").then((r) => setGoogle(r.data)).catch(() => {}); }, []);

  const soumettre = async (e) => {
    e.preventDefault();
    setErreur(""); setEnvoi(true);
    try { await connexion(email, mdp); navigate("/"); } catch (err) { setErreur(messageErreur(err)); } finally { setEnvoi(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr]">
      <section className="hidden lg:flex flex-col justify-between p-12">
        <Logo />
        <div className="max-w-xl at-rise">
          <p className="at-label mb-4">CRM CEE nouvelle génération</p>
          <h1 className="text-5xl xl:text-6xl font-bold tracking-tight leading-[1.02]">Vous parlez,<br />le CRM agit.<br /><span className="at-muted">Vous validez.</span></h1>
          <div className="mt-10 max-w-sm"><EchelleTemperature /></div>
          <p className="at-muted mt-3 text-sm max-w-sm">Du premier appel à la prime versée : chaque fiche se réchauffe à mesure qu'elle avance.</p>
        </div>
        <p className="at-muted text-sm">Airthermo · Bretagne</p>
      </section>
      <section className="flex flex-col justify-center px-5 py-10 sm:px-10">
        <div className="lg:hidden mb-10"><Logo /></div>
        <form onSubmit={soumettre} className="at-card w-full max-w-md mx-auto lg:mx-0 p-6 sm:p-9 space-y-5 at-rise" data-testid="login-form">
          <div>
            <h2 className="text-2xl font-bold tracking-tight">Connexion</h2>
            <p className="at-muted text-sm mt-1">Accédez à votre espace Airthermo.</p>
          </div>
          <Champ label="E-mail" id="email" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email-input" />
          <Champ label="Mot de passe" id="mdp" type="password" autoComplete="current-password" required value={mdp} onChange={(e) => setMdp(e.target.value)} data-testid="login-password-input" />
          {erreur && <p className="text-sm font-semibold" style={{ color: "var(--danger)" }} data-testid="login-error">{erreur}</p>}
          <button type="submit" disabled={envoi} className="at-btn at-btn-primary w-full" data-testid="login-submit-button">{envoi ? "Connexion…" : "Se connecter"}</button>
          <button type="button" onClick={() => setOubli(true)} className="w-full min-h-[44px] text-sm font-semibold at-muted" data-testid="login-forgot-button">Mot de passe oublié ?</button>
          <div className="flex items-center gap-3 at-muted text-xs"><span className="flex-1 h-px" style={{ background: "var(--line)" }} />ou<span className="flex-1 h-px" style={{ background: "var(--line)" }} /></div>
          <button type="button" disabled={!google.disponible} onClick={() => toast(google.message)} className="at-btn at-btn-ghost w-full" data-testid="login-google-button">
            <GoogleIcone />Se connecter avec Google
          </button>
          {!google.disponible && <p className="text-xs at-muted text-center" data-testid="login-google-note">Disponible au lot 7, une fois les identifiants Google saisis.</p>}
        </form>
      </section>
      <MotDePasseOublie ouvert={oubli} setOuvert={setOubli} />
    </div>
  );
}
